import type { SerializedFile } from "@repo/models";

import { logInfo } from "./logger";

export type ImageProcessType = "image/jpeg" | "image/png";

export interface ImageProcessOptions {
	/** cap output size */
	maxBytes?: number;
	/** longest side in pixels */
	maxDimension?: number;
	/** output mime */
	type?: ImageProcessType;
	/** starting quality for jpeg */
	initialQuality?: number;
	/** lowest quality to try */
	minQuality?: number;
	/** decrement when too big */
	qualityStep?: number;
	/** multiplicative factor for downscaling when quality exhausted */
	scaleStep?: number;
	/** don't scale below this */
	minDimension?: number;
}

const defaultOptions: Required<ImageProcessOptions> = {
	maxBytes: 3 * 1024 * 1024, // 3MB
	maxDimension: 2048,
	type: "image/jpeg",
	initialQuality: 0.9,
	minQuality: 0.7,
	qualityStep: 0.05,
	scaleStep: 0.9,
	minDimension: 512,
};

export async function processImageFileToDataUrl(
	file: File,
	opts?: ImageProcessOptions,
): Promise<{ dataUrl: string; type: ImageProcessType }> {
	const options = { ...defaultOptions, ...opts } as Required<ImageProcessOptions>;

	if (file.size <= options.maxBytes) {
		logInfo(
			`[image.ts] processImageFileToDataUrl: file size (${file.size} bytes) <= maxBytes (${options.maxBytes} bytes). Returning original.`,
		);
		const dataUrl = await blobToDataUrl(file);
		const type = (file.type || options.type) as ImageProcessType;
		return { dataUrl, type };
	}

	logInfo(
		`[image.ts] processImageFileToDataUrl: file size (${file.size} bytes) > maxBytes (${options.maxBytes} bytes). Processing...`,
	);
	const img = await loadImageFromFile(file);
	const { width: srcW, height: srcH } = getImageSize(img);

	const [targetW, targetH] = calculateFitSize(srcW, srcH, options.maxDimension);
	logInfo(`[image.ts] processImageFileToDataUrl: srcSize: ${srcW}x${srcH}, targetSize: ${targetW}x${targetH}`);

	let canvas = document.createElement("canvas");
	canvas.width = targetW;
	canvas.height = targetH;

	await resizeWithCanvas(img, canvas);

	// toBlob ignores quality for lossless PNG, so re-encoding at a lower quality cannot shrink it.
	const canReduceQuality = options.type !== "image/png";
	let quality = options.initialQuality;
	let blob = await canvasToBlob(canvas, options.type, quality);
	logInfo(`[image.ts] processImageFileToDataUrl: initial blob size: ${blob.size} bytes at quality: ${quality}`);

	while (blob.size > options.maxBytes) {
		// first reduce quality
		if (canReduceQuality && quality > options.minQuality) {
			quality = Math.max(options.minQuality, quality - options.qualityStep);
			blob = await canvasToBlob(canvas, options.type, quality);
			logInfo(
				`[image.ts] processImageFileToDataUrl: reduced quality to ${quality}, new size: ${blob.size} bytes`,
			);
			continue;
		}
		// then reduce dimensions
		const nextW = Math.max(options.minDimension, Math.round(canvas.width * options.scaleStep));
		const nextH = Math.max(options.minDimension, Math.round(canvas.height * options.scaleStep));
		if (nextW === canvas.width && nextH === canvas.height) {
			logInfo(`[image.ts] processImageFileToDataUrl: cannot shrink further: ${canvas.width}x${canvas.height}`);
			break; // can't shrink further
		}

		const smaller = document.createElement("canvas");
		smaller.width = nextW;
		smaller.height = nextH;
		await resizeWithCanvas(canvas, smaller);
		canvas = smaller;
		blob = await canvasToBlob(canvas, options.type, quality);
		logInfo(
			`[image.ts] processImageFileToDataUrl: reduced dimensions to ${nextW}x${nextH}, new size: ${blob.size} bytes`,
		);
	}

	logInfo(
		`[image.ts] processImageFileToDataUrl: final size: ${blob.size} bytes, quality: ${quality}, dimensions: ${canvas.width}x${canvas.height}`,
	);
	const dataUrl = await blobToDataUrl(blob);
	return { dataUrl, type: options.type };
}

export async function processImageFileToSerialized(file: File, opts?: ImageProcessOptions): Promise<SerializedFile> {
	const { dataUrl, type } = await processImageFileToDataUrl(file, opts);
	return {
		name: file.name,
		type,
		url: dataUrl,
	} as SerializedFile;
}

export function calculateFitSize(width: number, height: number, maxDimension: number) {
	if (width <= maxDimension && height <= maxDimension) return [width, height] as const;
	const ratio = width / height;
	if (ratio > 1) return [maxDimension, Math.round(maxDimension / ratio)] as const;
	return [Math.round(maxDimension * ratio), maxDimension] as const;
}

export function getImageSize(img: HTMLImageElement | HTMLCanvasElement) {
	const { naturalWidth, naturalHeight } = img as Partial<HTMLImageElement>;
	return { width: naturalWidth ?? img.width, height: naturalHeight ?? img.height };
}

function getSmoothedContext(canvas: HTMLCanvasElement) {
	const ctx = canvas.getContext("2d");
	if (ctx) {
		ctx.imageSmoothingEnabled = true;
		ctx.imageSmoothingQuality = "high";
	}
	return ctx;
}

function drawScaled(from: HTMLCanvasElement, toW: number, toH: number) {
	const next = document.createElement("canvas");
	next.width = toW;
	next.height = toH;
	getSmoothedContext(next)?.drawImage(from, 0, 0, toW, toH);
	return next;
}

/**
 * SS: 12/15/2025
 *
 * This is an AI-generated function
 *
 * This was written to remove Pica as it has not been updated in 4-5 years, so filling a bug report will not resolve the issue.
 * This might have worse performance, but Pica does not seem to handle Samsung phone/tablet images correctly as it causes them to
 * tile/corrupt.
 *
 * @param src
 * @param dest
 */
async function resizeWithCanvas(src: HTMLImageElement | HTMLCanvasElement, dest: HTMLCanvasElement) {
	const { width: srcW, height: srcH } = getImageSize(src);
	const dstW = dest.width;
	const dstH = dest.height;

	// If sizes match, draw directly
	if (srcW === dstW && srcH === dstH) {
		const ctx = getSmoothedContext(dest);
		if (ctx) {
			ctx.clearRect(0, 0, dstW, dstH);
			ctx.drawImage(src, 0, 0);
		}
		return;
	}

	// Progressive downscale in ~1/2 steps to improve quality when reducing a lot
	let currentCanvas: HTMLCanvasElement;
	if (src instanceof HTMLCanvasElement) {
		currentCanvas = src;
	} else {
		currentCanvas = document.createElement("canvas");
		currentCanvas.width = srcW;
		currentCanvas.height = srcH;
		currentCanvas.getContext("2d")?.drawImage(src, 0, 0);
	}

	// Downscale in steps until close to destination
	while (currentCanvas.width / 2 > dstW && currentCanvas.height / 2 > dstH) {
		const stepW = Math.max(dstW, Math.floor(currentCanvas.width / 2));
		const stepH = Math.max(dstH, Math.floor(currentCanvas.height / 2));
		currentCanvas = drawScaled(currentCanvas, stepW, stepH);
	}

	// Final draw into destination
	const dctx = getSmoothedContext(dest);
	if (dctx) {
		dctx.clearRect(0, 0, dstW, dstH);
		dctx.drawImage(currentCanvas, 0, 0, dstW, dstH);
	}
}

export function loadImageFromFile(file: File): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.addEventListener("load", () => {
			const img = new Image();
			img.addEventListener("load", () => resolve(img));
			img.addEventListener("error", () => reject(new Error("Failed to load image")));
			img.src = reader.result as string;
		});
		reader.addEventListener("error", () => reject(new Error("Failed to read file")));
		reader.readAsDataURL(file);
	});
}

export function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
	return new Promise((resolve, reject) => {
		canvas.toBlob(
			(blob) => {
				if (!blob) return reject(new Error("toBlob produced null"));
				resolve(blob);
			},
			type,
			quality,
		);
	});
}

export function blobToDataUrl(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.addEventListener("load", () => resolve(reader.result as string));
		reader.addEventListener("error", () => reject(new Error("Failed to read blob")));
		reader.readAsDataURL(blob);
	});
}
